import type { OMSConfig, TradeType, OptionType, StrategyEvaluator, SignalResult } from '../types';

import { evaluateConnectingDotsBreakout } from './ConnectingDotsBreakout';
import { evaluateIronCondor } from './IronCondor';
import { evaluateThetaScalp } from './ThetaScalp';
import { evaluateMinMaxItmTop5 } from './MinMaxItmTop5';
import { evaluateMinMaxItmTop5Sell } from './MinMaxItmTop5Sell';
import { evaluateConnectingDotsBase } from './ConnectingDotsBase';
import { evaluateConnectingDotsRSI } from './ConnectingDotsRSI';
import { evaluateConnectingDots5MLows } from './ConnectingDots5MLows';
import { evaluateConnectingDotsItmFirst } from './ConnectingDotsItmFirst';
import { evaluateConnectingDotsItmTop5 } from './ConnectingDotsItmTop5';
import { evaluateDominantBaseTrend } from './DominantBaseTrend';
import { evaluateDominanceSummary } from './DominanceSummary';
import { evaluateStrikeCountCounts } from './StrikeCountCounts';
import { evaluateStrikeRangeShift } from './StrikeRangeShift';
import { evaluateStrikeActivityRecent } from './StrikeActivityRecent';
import { evaluateCombinedCountAndShift } from './CombinedCountAndShift';
import { evaluateCombinedMinMaxAndDots } from './CombinedMinMaxAndDots';
import { evaluateMarketTrendActivity } from './MarketTrendActivity';
import { evaluateMarketWatchActivityShift } from './MarketWatchActivityShift';
import { evaluateStraddleBreakout } from './StraddleBreakout';
import { evaluateSRBounce } from './SRBounce';
import { evaluatePanicDivergence } from './PanicDivergence';
import { evaluateConnectingDotsExhaustion } from './ConnectingDotsExhaustion';
import { evaluateHolyGrailDoubleConfirmation } from './HolyGrailDoubleConfirmation';
import { evaluateZeroGravityVoidBreakout } from './ZeroGravityVoidBreakout';
import { evaluateSmartMoneyTrap } from './SmartMoneyTrap';
import { evaluateDoubleDropGammaSqueeze } from './DoubleDropGammaSqueeze';
import { evaluateSellerTrapSqueeze } from './SellerTrapSqueeze';
import { evaluateWhaleMomentumBreakout } from './WhaleMomentumBreakout';
import { evaluateOptionsScalperMode } from './OptionsScalperMode';
import { evaluateMultiTimeframeConfluence } from './MultiTimeframeConfluence';
import { evaluateIVExpansionDetector } from './IVExpansionDetector';
import { evaluateExpiryGammaBurst } from './ExpiryGammaBurst';
export const strategyEvaluators: Record<string, StrategyEvaluator> = {
    'connecting_dots_base': evaluateConnectingDotsBase,
    'connecting_dots_rsi': evaluateConnectingDotsRSI,
    'connecting_dots_5m_low': evaluateConnectingDots5MLows,
    'connecting_dots_itm_first': evaluateConnectingDotsItmFirst,
    'min_max_itm_top5': evaluateMinMaxItmTop5,
    'min_max_itm_top5_sell': evaluateMinMaxItmTop5Sell,
    'connecting_dots_itm_top5': evaluateConnectingDotsItmTop5,
    'connecting_dots_breakout': evaluateConnectingDotsBreakout,
    'dominant_base_trend': evaluateDominantBaseTrend,
    'dominance_summary': evaluateDominanceSummary,
    'strike_count_counts': evaluateStrikeCountCounts,
    'strike_range_shift': evaluateStrikeRangeShift,
    'strike_activity_recent': evaluateStrikeActivityRecent,
    'combined_count_shift': evaluateCombinedCountAndShift,
    'combined_minmax_dots': evaluateCombinedMinMaxAndDots,
    'market_trend_activity': evaluateMarketTrendActivity,
    'marketwatch_activity_shift': evaluateMarketWatchActivityShift,
    'iron_condor': evaluateIronCondor,
    'theta_scalp': evaluateThetaScalp,
    'straddle_breakout': evaluateStraddleBreakout,
    'sr_bounce': evaluateSRBounce,
    'panic_divergence': evaluatePanicDivergence,
    'connecting_dots_exhaustion': evaluateConnectingDotsExhaustion,
    'holy_grail_double_confirmation': evaluateHolyGrailDoubleConfirmation,
    'zero_gravity_void_breakout': evaluateZeroGravityVoidBreakout,
    'smart_money_trap': evaluateSmartMoneyTrap,
    'double_drop_gamma_squeeze': evaluateDoubleDropGammaSqueeze,
    'seller_trap_squeeze': evaluateSellerTrapSqueeze,
    'whale_momentum_breakout': evaluateWhaleMomentumBreakout,
    'options_scalper_mode': evaluateOptionsScalperMode,
    'multi_timeframe_confluence': evaluateMultiTimeframeConfluence,
    'iv_expansion_detector': evaluateIVExpansionDetector,
    'expiry_gamma_burst': evaluateExpiryGammaBurst
};

/** Centralized display names for all strategies. Import this instead of duplicating. */
export const strategyDisplayNames: Record<string, string> = {
    'connecting_dots_base': 'Connecting Dots Base',
    'connecting_dots_rsi': 'Connecting Dots RSI',
    'connecting_dots_5m_low': 'Connecting Dots 5M Lows',
    'connecting_dots_itm_first': 'Connecting Dots ITM First',
    'min_max_itm_top5': 'MIN/MAX ITM Top5',
    'min_max_itm_top5_sell': 'MIN/MAX ITM Top5 Sell',
    'connecting_dots_itm_top5': 'Connecting Dots (Top 5 ITM)',
    'connecting_dots_breakout': 'Connecting Dots Breakout',
    'dominant_base_trend': 'Dominant Base Trend',
    'dominance_summary': 'Dominance Summary',
    'strike_count_counts': 'Strike Count',
    'strike_range_shift': 'Strike Range Shift',
    'strike_activity_recent': 'Strike Activity (Recent 2)',
    'combined_count_shift': 'Combined Count + Shift',
    'combined_minmax_dots': 'Combined MIN/MAX + Dots',
    'market_trend_activity': 'Market Trend (Strike Activity)',
    'marketwatch_activity_shift': 'Activity Shift (MarketWatch)',
    'iron_condor': 'Iron Condor (Neutral)',
    'theta_scalp': 'Theta Scalp (Premium Decay)',
    'straddle_breakout': 'Straddle Breakout (Volatility)',
    'sr_bounce': 'S/R Bounce (Mean Reversion)',
    'panic_divergence': 'Panic Divergence (Trend Follower)',
    'connecting_dots_exhaustion': 'Connecting Dots Exhaustion (Contrarian Scalp)',
    'holy_grail_double_confirmation': 'Holy Grail Double Confirmation (High Conviction)',
    'zero_gravity_void_breakout': 'Zero-Gravity Void Breakout (Momentum)',
    'smart_money_trap': 'Smart Money Trap (Variance Div)',
    'double_drop_gamma_squeeze': 'Double Drop Gamma Squeeze',
    'seller_trap_squeeze': 'Seller Trap Squeeze (Short Covering)',
    'whale_momentum_breakout': 'Whale Breakout (Institutional Buy)',
    'options_scalper_mode': 'Scalper Mode (Micro-Trend Momentum)',
    'multi_timeframe_confluence': '3-Tick Confluence (High Conviction)',
    'iv_expansion_detector': 'IV Expansion Detector (Safe Premium)',
    'expiry_gamma_burst': '0DTE Gamma Burst'
};
